// Service Worker registration and update coordination for the guest PWA.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

const UPDATE_POLICY_TIMEOUT_MS = 1000;
const NORMAL_UPDATE_POLICY = 'normal';
const listeners = new Set();
const observedInstallingWorkers = new WeakSet();

let snapshot = {
  registration: null,
  waiting: null,
  policy: NORMAL_UPDATE_POLICY,
};
let serviceWorkerMessageListenerAttached = false;
let knownRegistration = null;
let reloading = false;

function publish(nextSnapshot) {
  snapshot = nextSnapshot;
  listeners.forEach((listener) => listener());
}

function clearUpdateNotice() {
  publish({
    registration: snapshot.registration,
    waiting: null,
    policy: NORMAL_UPDATE_POLICY,
  });
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot() {
  return snapshot;
}

function reloadThisTab() {
  if (reloading) {
    return;
  }

  reloading = true;
  window.location.reload();
}

function requestUpdatePolicy(worker) {
  if (typeof MessageChannel === 'undefined') {
    return Promise.resolve(NORMAL_UPDATE_POLICY);
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(() => resolve(NORMAL_UPDATE_POLICY), UPDATE_POLICY_TIMEOUT_MS);

    channel.port1.onmessage = (event) => {
      window.clearTimeout(timeout);
      resolve(event.data?.type === 'UPDATE_POLICY' && event.data.policy === 'urgent'
        ? 'urgent'
        : NORMAL_UPDATE_POLICY);
    };

    try {
      worker.postMessage({ type: 'GET_UPDATE_POLICY' }, [channel.port2]);
    } catch (error) {
      window.clearTimeout(timeout);
      resolve(NORMAL_UPDATE_POLICY);
    }
  });
}

function announceWaitingWorker(registration, worker) {
  if (!worker || snapshot.waiting === worker) {
    return;
  }

  requestUpdatePolicy(worker).then((policy) => {
    if (registration.waiting !== worker) {
      return;
    }

    publish({ registration, waiting: worker, policy });
  });
}

function inspectRegistration(registration, config) {
  if (registration.waiting) {
    announceWaitingWorker(registration, registration.waiting);
  }

  const installingWorker = registration.installing;
  if (!installingWorker || observedInstallingWorkers.has(installingWorker)) {
    return;
  }

  observedInstallingWorkers.add(installingWorker);
  installingWorker.onstatechange = () => {
    if (installingWorker.state !== 'installed') {
      return;
    }

    if (navigator.serviceWorker.controller) {
      announceWaitingWorker(registration, registration.waiting || installingWorker);
    } else if (config?.onSuccess) {
      config.onSuccess(registration);
    }
  };
}

function announceKnownWaitingWorker() {
  const registrationPromise = knownRegistration
    ? Promise.resolve(knownRegistration)
    : navigator.serviceWorker.getRegistration();

  registrationPromise.then((registration) => {
    if (registration?.waiting) {
      knownRegistration = registration;
      announceWaitingWorker(registration, registration.waiting);
    }
  });
}

function attachServiceWorkerListeners() {
  if (serviceWorkerMessageListenerAttached) {
    return;
  }

  serviceWorkerMessageListenerAttached = true;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'URGENT_UPDATE_AVAILABLE') {
      announceKnownWaitingWorker();
    }
    if (event.data?.type === 'URGENT_UPDATE_ACTIVATED') {
      reloadThisTab();
    }
  });
}

export function activateWaitingUpdate() {
  const { registration, waiting } = snapshot;
  if (!registration || !waiting || registration.waiting !== waiting) {
    if (registration?.waiting) {
      announceWaitingWorker(registration, registration.waiting);
    } else {
      clearUpdateNotice();
    }
    return false;
  }

  if (snapshot.policy !== 'urgent') {
    return false;
  }

  try {
    waiting.postMessage({ type: 'ACTIVATE_URGENT_UPDATE' });
    return true;
  } catch (error) {
    return false;
  }
}

export function register(config) {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
      navigator.serviceWorker.ready.then(() => {
        console.log('Wedding Photo App is running in offline mode.');
      });
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      knownRegistration = registration;
      attachServiceWorkerListeners();
      inspectRegistration(registration, config);
      setInterval(() => registration.update(), 60 * 1000);
      registration.onupdatefound = () => inspectRegistration(registration, config);
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}

export const __testing = {
  announceWaitingWorker,
  attachServiceWorkerListeners,
  clearUpdateNotice,
  requestUpdatePolicy,
  reset() {
    snapshot = { registration: null, waiting: null, policy: NORMAL_UPDATE_POLICY };
    reloading = false;
    listeners.clear();
  },
  setSnapshot(nextSnapshot) {
    snapshot = nextSnapshot;
  },
};
